import React, { useEffect, useMemo } from "react";
import { useAppSelector } from "../../Store";
import { Graph_QL } from "../../Services";
import { useFeedCurves } from "../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves";
import { useAnimalGroups } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimalGroups";
import { useSystem } from "../../Services/Agrisys_GqlDataCaller/SystemCalls/useSystem";
import { useMilestones } from "../../Services/Agrisys_GqlDataCaller/MilestoneDataCalls/useMilestone";
import { useSection } from "../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections";

const useSelectOptions = (params?: { includeUndefinedOption?: boolean; systemId?: number }) => {
  const { includeUndefinedOption = false, systemId } = params ?? {};
  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);

  const { call: getFeedCurves, data: feedCurvesState } = useFeedCurves(connection);
  const { call: getAnimalGroups, data: animalGroupsState } = useAnimalGroups(connection);
  const { call: getSystemsInfo, data: systemsState } = useSystem(connection);
  const { call: getMilestoneInfo, data: milestonesState} = useMilestones(connection);
  const { call: getLfSectionInfo, data: sectionState } = useSection(connection);

  useEffect(() => {
    getFeedCurves({fetchAmount: 50, ifFeedCurveBaseInfoOnly: true})
    getAnimalGroups({fetchAmount: 50, ifIncludeAnimalCount: true})
    getSystemsInfo({fetchAmount: 50})
    getMilestoneInfo({fetchAmount: 50})
    getLfSectionInfo({fetchAmount: 50})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const feedcurveOptions = useMemo(() => {
    if (feedCurvesState && feedCurvesState[domain]) {
      return feedCurvesState[domain].data.map(feedcurve => ({
        label: feedcurve.name,
        id: feedcurve.feedCurveId
      }))
    }
    return []
  },[domain, feedCurvesState])

  const groupOptions = useMemo(() => {
    if (animalGroupsState && animalGroupsState[domain]) {
      const groups = animalGroupsState[domain].data.map(group => ({
        label: group.name,
        id: group.animalGroupId,
      }));

      if (includeUndefinedOption) {
        return [{ label: "undefined", id: undefined }, ...groups];
      }

      return groups;
    }
    return includeUndefinedOption ? [{ label: "undefined", id: undefined }] : [];
  }, [domain, animalGroupsState, includeUndefinedOption]);
  
  const systemOptions = useMemo(() => {
    if (systemsState && systemsState[domain]) {
      return systemsState[domain].data.map(system => ({
        label: system.name,
        id: system.systemId
      }))
    }
    return []
  },[domain, systemsState])

  const milestoneOptions = useMemo(() => {
    if (milestonesState && milestonesState[domain]) {
      return milestonesState[domain].data.map(milestone => ({
        label: milestone.name,
        id: milestone.milestone_id
      }))
    }
    return []
  }, [domain, milestonesState])
  
  const sectionOptions = useMemo(() => {
    if (sectionState && sectionState[domain]) {
      return sectionState[domain].data
        .filter(section => systemId === undefined || section.system_id === systemId)
        .map(section => ({
          label: section.name,
          id: section.section_id
        })) 
    }
    return []
  }, [domain, sectionState, systemId])
  
  const locationOptions = useMemo(() => {
    if (sectionState && sectionState[domain]) {
      const locations = sectionState[domain].data
        .flatMap(section => section.section_locations)
        .map(({ name, location_id }) => ({ label: name, id: location_id }));

      if (includeUndefinedOption) {
        return [{ label: "undefined", id: undefined }, ...locations];
      }

      return locations;
    }
    return includeUndefinedOption ? [{ label: "undefined", id: undefined }] : [];
  }, [domain, sectionState, includeUndefinedOption]);

  return {
    feedcurveOptions,
    groupOptions,
    systemOptions,
    milestoneOptions,
    locationOptions,
    sectionOptions
  }
}

export default useSelectOptions